import { io } from 'socket.io-client';

/**
 * The app's single socket.io connection.
 *
 * Imperative and React-free, like `api.js` next to it: the hook that owns the
 * lifecycle lives in `src/hooks/useNotificationsSocket.js`.
 *
 * One connection is enough for every notification the backend can send. The
 * server subscribes to Redis with the pattern `*_{role}_{id}` and re-emits each
 * message under its full channel name, so `download_admin_7`,
 * `shipping_zone_coverage_admin_7` and anything added later all arrive here
 * without asking for them. The legacy front mounts its hook once per listening
 * component and opens three sockets to receive one channel each.
 */

let socket = null;
let refCount = 0;
let session = null;

// Overwritten on every `connectSocket` so the newest closure wins. The
// connection outlives any single render; the callbacks do not.
let handlers = { onEvent: () => {}, onStatusChange: () => {} };

/**
 * Opens the connection, or joins the one already open.
 *
 * @param {object} params
 * @param {number|string} params.userId - `state.auth.user.id`.
 * @param {string} params.role - Half of the channel name the backend publishes to.
 * @param {(channel: string, payload: object) => void} params.onEvent
 * @param {(isConnected: boolean) => void} params.onStatusChange
 */
export const connectSocket = ({ userId, role, onEvent, onStatusChange }) => {
  handlers = { onEvent, onStatusChange };
  refCount += 1;

  if (socket) {
    onStatusChange(socket.connected);
    return;
  }

  session = { userId, role };

  socket = io(process.env.REACT_APP_SOCKET_SERVER, {
    autoConnect: false,
    transports: ['websocket', 'polling'],
  });

  // Emitted on every connect, not just the first: the server joins the room
  // inside its own `open` handler, so a reconnect that skipped this would sit
  // in no room and receive nothing while looking perfectly healthy.
  socket.on('connect', () => {
    socket.emit('open', { id: session.userId, role: session.role });
    handlers.onStatusChange(true);
  });

  socket.on('disconnect', () => handlers.onStatusChange(false));

  // `onAny` rather than `socket.on(channel)`: the event name *is* the Redis
  // channel, prefix included, so one listener covers every producer and the
  // routing happens in the reducer.
  socket.onAny((channel, payload) => handlers.onEvent(channel, payload));

  socket.connect();
};

/**
 * Releases one hold on the connection, closing it when the last one goes.
 *
 * The count is what keeps a second caller from closing a connection the first
 * one is still using. StrictMode's development remount goes through here too,
 * and simply rebuilds the socket — the churn is invisible and only happens in
 * dev, but it is why `connectSocket` has to tolerate being called on a live
 * connection.
 */
export const disconnectSocket = () => {
  refCount = Math.max(0, refCount - 1);
  if (refCount > 0 || !socket) return;

  if (socket.connected && session) {
    socket.emit('close', { id: session.userId, role: session.role });
  }

  socket.offAny();
  socket.disconnect();
  socket = null;
  session = null;
  handlers.onStatusChange(false);
};
